import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Studio } from '../../types/studio';
import { NavigationState, NavigationHistoryItem } from './types';

const initialState: NavigationState = {
  currentPath: '/',
  previousPath: '',
  mobileMenuOpen: false,
  activeStudio: null,
  navigationHistory: [],
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  /* eslint-disable no-param-reassign */
  reducers: {
    setCurrentPath: (state, action: PayloadAction<string>) => {
      state.previousPath = state.currentPath;
      state.currentPath = action.payload;
    },

    setActiveStudio: (state, action: PayloadAction<Studio | null>) => {
      state.activeStudio = action.payload;
    },

    toggleMobileMenu: state => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },

    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload;
    },

    addToHistory: (state, action: PayloadAction<NavigationHistoryItem>) => {
      // Add new item to the beginning of the array
      state.navigationHistory.unshift(action.payload);
      // Keep only the last 50 items for performance
      if (state.navigationHistory.length > 50) {
        state.navigationHistory = state.navigationHistory.slice(0, 50);
      }
    },

    clearHistory: state => {
      state.navigationHistory = [];
    },

    // Action to handle route changes and automatically update history
    navigateToPath: (
      state,
      action: PayloadAction<{ path: string; studioContext?: Studio }>
    ) => {
      const { path, studioContext } = action.payload;

      // Update current and previous paths
      state.previousPath = state.currentPath;
      state.currentPath = path;

      // Add to navigation history
      const historyItem: NavigationHistoryItem = {
        path,
        timestamp: Date.now(),
        studioContext: studioContext || state.activeStudio || undefined,
      };

      state.navigationHistory.unshift(historyItem);
      if (state.navigationHistory.length > 50) {
        state.navigationHistory = state.navigationHistory.slice(0, 50);
      }

      // Close mobile menu on navigation (UX improvement)
      state.mobileMenuOpen = false;
    },

    // Reset navigation state (useful for logout)
    resetNavigationState: () => initialState,
  },
  /* eslint-enable no-param-reassign */
});

export const {
  setCurrentPath,
  setActiveStudio,
  toggleMobileMenu,
  setMobileMenuOpen,
  addToHistory,
  clearHistory,
  navigateToPath,
  resetNavigationState,
} = navigationSlice.actions;

export default navigationSlice.reducer;
